import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";

import {
  Check,
  ArrowRight,
  User,
  Users,
  Building2,
  ShieldCheck,
  FileSpreadsheet,
  FileText,
  Pencil,
  Trash2,
  GripVertical,
  Calendar,
  Bell,
  ChevronDown,
  Info,
  Download,
  Settings,
  Search,
  Plus,
} from "lucide-react";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const STEPS = ["Upload Document", "Add Recipients", "Review & Send"];

// The backend only knows Signer and Approver (anything else would silently become a Signer).
const ROLES = ["Signer", "Approver"];
const NOTIFY = ["Email", "SMS", "Email + SMS"];
const AUTH = ["Digital Certificate (PKI)", "Email OTP", "Mobile OTP", "ID Verification"];
const COUNTRIES = [
  { code: "+91", label: "IN +91" },
  { code: "+1", label: "US +1" },
  { code: "+44", label: "UK +44" },
  { code: "+61", label: "AU +61" },
  { code: "+65", label: "SG +65" },
  { code: "+971", label: "AE +971" },
  { code: "+49", label: "DE +49" },
];
const REMINDERS = [
  { value: "none", label: "No reminders" },
  { value: "1", label: "Every day" },
  { value: "3", label: "Every 3 days" },
  { value: "7", label: "Every week" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AVATAR_COLORS = ["bg-indigo-500", "bg-purple-500", "bg-emerald-500", "bg-amber-500", "bg-sky-500", "bg-rose-500"];

const SAMPLE_CSV =
  "name,email,mobile,role,notification_preference,authentication_method\n" +
  "Ravi Kumar,ravi.kumar@acme.com,+919876543210,Signer,Email + SMS,Digital Certificate (PKI)\n" +
  "Priya Nair,priya.nair@acme.com,,Approver,Email,Email OTP\n";

const toDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const in30Days = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d;
};

const initials = (name, email) => {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts.length > 1 ? parts[1][0] : "")).toUpperCase();
};

const extOf = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

// Small CSV parser (handles quoted cells)
const feedQuoted = (s, c, next) => {
  if (c !== '"') {
    s.cell += c;
    return false;
  }
  if (next === '"') {
    s.cell += '"';
    return true; // the escaped quote used up the next character as well
  }
  s.quoted = false;
  return false;
};

function parseCsv(text) {
  const rows = [];
  const s = { row: [], cell: "", quoted: false };
  const endCell = () => {
    s.row.push(s.cell);
    s.cell = "";
  };
  const endRow = () => {
    endCell();
    if (s.row.some((x) => x.trim() !== "")) rows.push(s.row);
    s.row = [];
  };

  // Returns true when the next character has been consumed as well.
  const feed = (c, next) => {
    if (s.quoted) return feedQuoted(s, c, next);
    if (c === '"') {
      s.quoted = true;
      return false;
    }
    if (c === ",") {
      endCell();
      return false;
    }
    if (c === "\n") {
      endRow();
      return false;
    }
    if (c === "\r") {
      endRow();
      return next === "\n";
    }
    s.cell += c;
    return false;
  };

  const chars = Array.from(text);
  let skip = false;
  chars.forEach((c, i) => {
    if (skip) {
      skip = false;
      return;
    }
    skip = feed(c, chars[i + 1]);
  });
  endRow();
  return rows;
}

const pick = (list, value, fallback) =>
  list.find((x) => x.toLowerCase() === String(value || "").trim().toLowerCase()) || fallback;

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

const inputCls =
  "w-full h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-800 bg-white outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

const Label = ({ children, required }) => (
  <label className="block text-xs font-medium text-gray-700 mb-1">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

// A bordered field with the icon as a normal flex child, so the icon can never sit on top of the text.
const IconField = ({ icon: Icon, chevron, children }) => (
  <div className="flex items-center gap-2 h-9 w-full border border-gray-200 rounded-lg px-3 bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">
    <Icon size={14} className="text-gray-500 shrink-0" />
    {children}
    {chevron && <ChevronDown size={14} className="text-gray-500 shrink-0 pointer-events-none -ml-[18px]" />}
  </div>
);
const bareCtl = { flex: 1, minWidth: 0, height: "100%", border: 0, padding: 0, margin: 0, background: "transparent", outline: "none", boxShadow: "none" };
const bareSelect = { ...bareCtl, paddingRight: 18, WebkitAppearance: "none", appearance: "none", cursor: "pointer" };

const Select = ({ value, onChange, children, className = "" }) => (
  <div className={`relative ${className}`}>
    <select
      value={value}
      onChange={onChange}
      className={`${inputCls} appearance-none pr-8`}
    >
      {children}
    </select>
    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
  </div>
);

const RolePill = ({ role }) => (
  <span
    className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
      role === "Approver" ? "bg-purple-100 text-purple-700" : "bg-indigo-100 text-indigo-700"
    }`}
  >
    {role}
  </span>
);

const Card = ({ children, className = "" }) => (
  <div className={`bg-white border border-gray-200 rounded-xl p-3 ${className}`}>{children}</div>
);

const CardTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between mb-2">
    <div className="flex items-center gap-2.5">
      <Icon size={17} className="text-indigo-600" />
      <h3 className="text-sm font-semibold text-gray-900">{children}</h3>
    </div>
    {right}
  </div>
);

/* ------------------------------------------------------------------ */
/* Pure helpers                                                        */
/* ------------------------------------------------------------------ */

const EMPTY_FORM = { name: "", email: "", cc: "+91", phone: "", role: "Signer", notify: "Email", auth: AUTH[0] };

const pluralS = (n) => (n === 1 ? "" : "s");

const pagesLabel = (pages) => (pages ? ` • ${pages} page${pluralS(pages)}` : "");

const formFromRecipient = (r) => ({
  name: r.name,
  email: r.email,
  cc: r.cc || "+91",
  phone: r.phone || "",
  role: r.role,
  notify: r.notify,
  auth: r.auth,
});

// Returns { error } or { data } for the "Add recipient" form.
const validateRecipientForm = (form, recipients, editingId) => {
  const name = form.name.trim();
  const email = form.email.trim().toLowerCase();
  const phone = form.phone.replace(/[\s-]/g, "");

  if (!name) return { error: "Please enter the recipient's name." };
  if (!EMAIL_RE.test(email)) return { error: "Please enter a valid email address." };
  if (phone && !/^\d{6,15}$/.test(phone)) return { error: "Mobile number should be 6-15 digits." };
  if (form.notify !== "Email" && !phone) return { error: "A mobile number is needed for SMS notifications." };
  if (recipients.some((r) => r.email === email && r.id !== editingId)) return { error: "This email is already in the list." };

  return { data: { name, email, cc: form.cc, phone, role: form.role, notify: form.notify, auth: form.auth } };
};

const checkOrgUser = (u, recipients) => {
  const email = (u.email || "").trim().toLowerCase();
  if (!EMAIL_RE.test(email)) return { error: "That user doesn't have a valid email address." };
  if (recipients.some((r) => r.email === email)) return { duplicate: true };
  return { email };
};

const makeOrgRecipient = (u, email, id) => ({
  id, name: u.name || email, email, cc: "", phone: "", role: "Signer", notify: "Email", auth: AUTH[0], selected: true,
});

const filterOrgUsers = (users, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return users;
  return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
};

const nextProblem = ({ uploadedFile, selectedCount, deadline }) => {
  if (!uploadedFile) return "No document found. Use “Change” to upload one.";
  if (selectedCount === 0) return "Please select at least one recipient.";
  if (!deadline || deadline < toDateInput(new Date())) return "Please choose a signing deadline that isn't in the past.";
  return "";
};

/* ---- CSV import ---- */

const CSV_COLUMNS = {
  name: [["name"], 0],
  email: [["email", "email address"], 1],
  phone: [["mobile", "mobile number", "phone"], 2],
  role: [["role"], 3],
  notify: [["notification_preference", "notification preference", "notification"], 4],
  auth: [["authentication_method", "authentication method", "auth"], 5],
};

const csvColumns = (header, hasHeader) =>
  Object.fromEntries(
    Object.entries(CSV_COLUMNS).map(([key, [names, fallback]]) => [
      key,
      hasHeader ? header.findIndex((h) => names.includes(h)) : fallback,
    ])
  );

// Returns { skip: reason } or { recipient }.
const csvRowToRecipient = (cells, idx, rowNo, seen, nextId) => {
  const get = (i) => (i >= 0 ? (cells[i] || "").trim() : "");
  const email = get(idx.email).toLowerCase();
  const roleRaw = get(idx.role);
  const role = roleRaw ? pick(ROLES, roleRaw, null) : "Signer";

  if (!EMAIL_RE.test(email)) return { skip: `row ${rowNo}: invalid email` };
  if (seen.has(email)) return { skip: `row ${rowNo}: duplicate email` };
  if (!role) return { skip: `row ${rowNo}: role "${roleRaw}" isn't supported (use Signer or Approver)` };

  seen.add(email);
  return {
    recipient: {
      id: nextId(),
      name: get(idx.name) || email,
      email,
      cc: "",
      phone: get(idx.phone).replace(/[\s-]/g, ""),
      role,
      notify: pick(NOTIFY, get(idx.notify), "Email"),
      auth: pick(AUTH, get(idx.auth), AUTH[0]),
      selected: true,
    },
  };
};

const parseRecipientRows = (rows, existingEmails, nextId) => {
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const hasHeader = header.includes("email");
  const idx = csvColumns(header, hasHeader);
  const seen = new Set(existingEmails);
  const added = [];
  const skipped = [];

  (hasHeader ? rows.slice(1) : rows).forEach((cells, n) => {
    const rowNo = n + (hasHeader ? 2 : 1);
    const result = csvRowToRecipient(cells, idx, rowNo, seen, nextId);
    if (result.skip) skipped.push(result.skip);
    else added.push(result.recipient);
  });
  return { added, skipped };
};

const skippedSummary = (skipped) => {
  if (!skipped.length) return "";
  const more = skipped.length > 3 ? "…" : "";
  return ` Skipped ${skipped.length}: ${skipped.slice(0, 3).join("; ")}${more}`;
};

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

function useStatus() {
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const fail = (msg) => {
    setNotice("");
    setError(msg);
  };
  const succeed = (msg) => {
    setError("");
    setNotice(msg);
  };
  return { error, notice, setError, setNotice, fail, succeed };
}

function useOrgUsers() {
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("user")) || {};
    if (!stored.email) return;
    axios
      .get(`${API_URL}/users`, { params: { email: stored.email } })
      .then((res) => setOrgUsers(res.data || []))
      .catch((err) => console.error("Fetch users error:", err));
  }, []);
  return orgUsers;
}

function useSettings(meta) {
  const [signingOrder, setSigningOrder] = useState(false);
  const [deadline, setDeadline] = useState(meta.deadline || toDateInput(in30Days()));
  const [reminder, setReminder] = useState(meta.reminder || "3");
  const [requireAll, setRequireAll] = useState(meta.requireAll ?? true);
  return { signingOrder, setSigningOrder, deadline, setDeadline, reminder, setReminder, requireAll, setRequireAll };
}

function useRecipientList({ fail, succeed, setError }) {
  const [recipients, setRecipients] = useState([]);
  const idRef = useRef(0);
  const nextId = () => `r-${++idRef.current}`;

  const addOrgUser = (u) => {
    const check = checkOrgUser(u, recipients);
    if (check.error) return fail(check.error);
    if (check.duplicate) return;
    setRecipients((prev) => [...prev, makeOrgRecipient(u, check.email, nextId())]);
    setError("");
  };

  const save = (data, editingId) => {
    if (editingId) setRecipients((prev) => prev.map((r) => (r.id === editingId ? { ...r, ...data } : r)));
    else setRecipients((prev) => [...prev, { id: nextId(), ...data, selected: true }]);
  };

  const remove = (id) => setRecipients((prev) => prev.filter((r) => r.id !== id));

  const toggleOne = (id) =>
    setRecipients((prev) => prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r)));

  const allSelected = recipients.length > 0 && recipients.every((r) => r.selected);
  const toggleAll = () => setRecipients((prev) => prev.map((r) => ({ ...r, selected: !allSelected })));

  const reorder = (result) => {
    if (!result.destination) return;
    const items = [...recipients];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setRecipients(items);
  };

  const importCsv = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) return fail("Please choose a .csv file.");

    const rows = parseCsv((await file.text()).replace(/^\uFEFF/, ""));
    if (!rows.length) return fail("The CSV file is empty.");

    const { added, skipped } = parseRecipientRows(rows, recipients.map((r) => r.email), nextId);
    const skippedText = skippedSummary(skipped);
    if (!added.length) return fail(`No recipients were imported.${skippedText}`);

    setRecipients((prev) => [...prev, ...added]);
    succeed(`Imported ${added.length} recipient${pluralS(added.length)}.${skippedText}`);
  };

  return { recipients, addOrgUser, save, remove, toggleOne, allSelected, toggleAll, reorder, importCsv };
}

/* ------------------------------------------------------------------ */
/* Components                                                          */
/* ------------------------------------------------------------------ */

const TAB_DEFS = [
  ["individual", "Individual", User],
  ["organization", "Organization", Building2],
];

const Stepper = ({ currentStep }) => (
  <div className="px-6 py-1">
    <div className="flex items-start">
      {STEPS.map((step, index) => {
        const reached = index <= currentStep;
        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== STEPS.length - 1 && (
              <div
                className={`absolute top-4 h-[2px] ${reached ? "bg-indigo-500" : "bg-gray-200"}`}
                style={{ left: "calc(50% + 22px)", width: "calc(100% - 44px)" }}
              />
            )}
            <div
              className={`z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                reached ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              {index < currentStep ? <Check size={14} /> : index + 1}
            </div>
            <p className={`mt-1.5 text-xs font-medium text-center ${index === currentStep ? "text-indigo-600" : "text-gray-500"}`}>
              {step}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

const RecipientTabs = ({ tab, onSelect }) => (
  <div className="flex items-center gap-6 border-b border-gray-200 mb-3">
    {TAB_DEFS.map(([key, label, Icon]) => (
      <button
        key={key}
        type="button"
        onClick={() => onSelect(key)}
        className={`flex items-center gap-1.5 pb-2 -mb-px text-xs font-medium border-b-2 transition ${
          tab === key ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"
        }`}
      >
        <Icon size={13} /> {label}
      </button>
    ))}
  </div>
);

const IndividualForm = ({ form, setField, editingId, onCancel, onSubmit }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
      <div>
        <Label required>Name</Label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder="Full name"
          className={inputCls}
        />
      </div>
      <div>
        <Label required>Email Address</Label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setField("email", e.target.value)}
          placeholder="name@company.com"
          className={inputCls}
        />
      </div>
      <div>
        <Label>Mobile Number</Label>
        <div className="flex gap-2">
          <Select value={form.cc} onChange={(e) => setField("cc", e.target.value)} className="w-[104px] shrink-0">
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </Select>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="9876543210"
            className={inputCls}
          />
        </div>
      </div>
      <div>
        <Label required>Role</Label>
        <Select value={form.role} onChange={(e) => setField("role", e.target.value)}>
          {ROLES.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </Select>
      </div>
      <div>
        <Label>Notification Preference</Label>
        <Select value={form.notify} onChange={(e) => setField("notify", e.target.value)}>
          {NOTIFY.map((n) => (
            <option key={n}>{n}</option>
          ))}
        </Select>
      </div>
      <div>
        <Label>Authentication Method</Label>
        <Select value={form.auth} onChange={(e) => setField("auth", e.target.value)}>
          {AUTH.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </Select>
      </div>
    </div>

    <div className="mt-2 bg-indigo-50/70 rounded-lg px-3 py-1.5 flex items-center gap-3 flex-wrap">
      <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center shrink-0">
        <ShieldCheck size={15} className="text-indigo-600" />
      </div>
      <p className="text-xs text-gray-600 flex-1 min-w-[200px] leading-relaxed">
        The recipient will receive a secure link to sign the document. They will be authenticated using the selected
        method.
      </p>
      <div className="flex items-center gap-2">
        {editingId && (
          <button type="button" onClick={onCancel} className="h-8 px-3 text-xs text-gray-600 hover:text-gray-800">
            Cancel edit
          </button>
        )}
        <button
          type="button"
          onClick={onSubmit}
          className="flex items-center gap-1.5 h-8 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition"
        >
          {editingId ? <Check size={13} /> : <Plus size={13} />}
          {editingId ? "Update Recipient" : "Add Recipient"}
        </button>
      </div>
    </div>
  </>
);

const OrgPicker = ({ orgUsers, query, onQuery, recipients, onAdd }) => {
  const filtered = filterOrgUsers(orgUsers, query);
  const emptyText = orgUsers.length === 0 ? "No organization members found." : "No one matches your search.";
  return (
    <div>
      <div className="mb-2">
        <IconField icon={Search}>
          <input
            type="text"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search people in your organization"
            className="text-sm text-gray-800"
            style={bareCtl}
          />
        </IconField>
      </div>
      <div className="border border-gray-200 rounded-lg max-h-[150px] overflow-y-auto divide-y divide-gray-100">
        {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-6">{emptyText}</p>}
        {filtered.map((u) => {
          const already = recipients.some((r) => r.email === (u.email || "").toLowerCase());
          return (
            <div key={u.id ?? u.email} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{u.name || u.email}</p>
                <p className="text-xs text-gray-500 truncate">{u.email}</p>
              </div>
              <button
                type="button"
                disabled={already}
                onClick={() => onAdd(u)}
                className={`h-7 px-3 rounded-md text-xs font-medium shrink-0 transition ${
                  already
                    ? "bg-green-50 text-green-600 cursor-default"
                    : "border border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                }`}
              >
                {already ? "Added" : "Add"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CsvUpload = ({ onFile }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFile = (file) => {
    if (inputRef.current) inputRef.current.value = "";
    onFile(file);
  };

  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([SAMPLE_CSV], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-recipients.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <CardTitle
        icon={Users}
        right={
          <button type="button" onClick={downloadSample} className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700">
            <Download size={13} /> Download Sample CSV
          </button>
        }
      >
        Add Multiple Recipients
      </CardTitle>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        className={`border-2 border-dashed rounded-xl px-4 py-2 flex items-center justify-center gap-4 transition ${
          drag ? "border-indigo-500 bg-indigo-50" : "border-indigo-200 bg-indigo-50/30"
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
          <FileSpreadsheet size={16} className="text-indigo-500" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-gray-800">Upload CSV</p>
          <p className="text-[11px] text-gray-500 mb-2">Add multiple recipients using a CSV file</p>
          <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="h-8 px-5 border border-indigo-400 text-indigo-600 hover:bg-indigo-50 rounded-lg text-xs font-medium transition"
          >
            Choose File
          </button>
        </div>
      </div>
    </div>
  );
};

const RecipientRow = ({ r, index, gridCols, signingOrder, stepNo, onToggle, onEdit, onDelete }) => (
  <Draggable draggableId={String(r.id)} index={index} isDragDisabled={!signingOrder}>
    {(p, snap) => (
      <div
        ref={p.innerRef}
        {...p.draggableProps}
        className={`grid ${gridCols} gap-2 items-center px-3 py-1.5 border-t border-gray-100 text-xs ${
          snap.isDragging ? "bg-white shadow-lg" : "bg-white"
        }`}
      >
        {signingOrder && (
          <div className="flex items-center gap-1 text-gray-500">
            <span {...p.dragHandleProps} className="cursor-grab">
              <GripVertical size={14} className="text-gray-400" />
            </span>
            <span className="font-medium">{r.selected ? stepNo : "–"}</span>
          </div>
        )}
        <input
          type="checkbox"
          checked={r.selected}
          onChange={() => onToggle(r.id)}
          className="w-3.5 h-3.5 accent-indigo-600"
        />
        <span className="font-medium text-gray-800 truncate" title={r.name}>
          {r.name}
        </span>
        <span className="text-gray-600 truncate" title={r.email}>
          {r.email}
        </span>
        <span>
          <RolePill role={r.role} />
        </span>
        <span className="text-gray-700 truncate" title={r.auth}>
          {r.auth}
        </span>
        <span>
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
              r.selected ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            {r.selected ? "Added" : "Pending"}
          </span>
        </span>
        <span className="flex items-center gap-2.5">
          <button type="button" onClick={() => onEdit(r)} title="Edit" className="text-indigo-500 hover:text-indigo-700">
            <Pencil size={13} />
          </button>
          <button type="button" onClick={() => onDelete(r.id)} title="Remove" className="text-red-400 hover:text-red-600">
            <Trash2 size={13} />
          </button>
        </span>
      </div>
    )}
  </Draggable>
);

const RecipientList = ({ list, selected, signingOrder, onEdit, onDelete }) => {
  const { recipients } = list;
  const gridCols = signingOrder
    ? "grid-cols-[46px_26px_1.1fr_1.6fr_84px_1.4fr_72px_56px]"
    : "grid-cols-[26px_1.1fr_1.6fr_84px_1.4fr_72px_56px]";
  const stepNumber = (id) => selected.findIndex((r) => r.id === id) + 1;

  return (
    <div>
      <CardTitle icon={Users}>
        Recipient List <span className="text-gray-500 font-medium">({recipients.length})</span>
      </CardTitle>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className={`grid ${gridCols} gap-2 items-center px-3 py-1.5 bg-gray-50 text-[11px] font-medium text-gray-500`}>
          {signingOrder && <span>Order</span>}
          <input type="checkbox" checked={list.allSelected} onChange={list.toggleAll} disabled={!recipients.length} className="w-3.5 h-3.5 accent-indigo-600" />
          <span>Name</span>
          <span>Email Address</span>
          <span>Role</span>
          <span>Authentication Method</span>
          <span>Status</span>
          <span>Action</span>
        </div>

        <DragDropContext onDragEnd={list.reorder}>
          <Droppable droppableId="recipients">
            {(dropProvided) => (
              <div {...dropProvided.droppableProps} ref={dropProvided.innerRef} className="max-h-[150px] overflow-y-auto">
                {recipients.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-6 px-4">
                    No recipients yet. Add one above, upload a CSV, or pick people from your organization.
                  </p>
                )}
                {recipients.map((r, index) => (
                  <RecipientRow
                    key={r.id}
                    r={r}
                    index={index}
                    gridCols={gridCols}
                    signingOrder={signingOrder}
                    stepNo={stepNumber(r.id)}
                    onToggle={list.toggleOne}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <p className="text-[11px] text-gray-500 mt-2">
        Showing {recipients.length} of {recipients.length} recipient{pluralS(recipients.length)}
        {recipients.length > 0 && ` · ${selected.length} selected`}
      </p>
    </div>
  );
};

const DocumentSummary = ({ uploadedFile, onChange }) => {
  const ext = extOf(uploadedFile?.name);
  return (
    <div>
      <CardTitle icon={FileText}>Document Summary</CardTitle>
      {uploadedFile ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-8 h-10 rounded-md flex items-center justify-center text-[10px] font-bold text-white shrink-0 ${
                ext === "pdf" ? "bg-red-500" : "bg-indigo-500"
              }`}
            >
              {(ext || "FILE").toUpperCase().slice(0, 4)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate" title={uploadedFile.name}>
                {uploadedFile.name}
              </p>
              <p className="text-xs text-gray-500">
                {uploadedFile.size}
                {pagesLabel(uploadedFile.pages)}
              </p>
            </div>
          </div>
          <button type="button" onClick={onChange} className="text-xs font-medium text-indigo-600 hover:text-indigo-700 shrink-0">
            Change
          </button>
        </div>
      ) : (
        <p className="text-xs text-gray-400">
          No document attached.{" "}
          <button type="button" onClick={onChange} className="text-indigo-600 font-medium">
            Upload one
          </button>
        </p>
      )}
    </div>
  );
};

const SelectedRecipients = ({ selected, signingOrder }) => (
  <div>
    <CardTitle icon={Users}>
      Selected Recipients <span className="text-gray-500 font-medium">({selected.length})</span>
    </CardTitle>
    {selected.length === 0 && <p className="text-xs text-gray-400">Tick recipients in the list to include them.</p>}
    {selected.length > 0 && (
      <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1">
        {selected.map((r, i) => (
          <div key={r.id} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className={`w-7 h-7 rounded-full text-white text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                  AVATAR_COLORS[i % AVATAR_COLORS.length]
                }`}
              >
                {initials(r.name, r.email)}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">
                  {signingOrder && <span className="text-gray-400 mr-1">{i + 1}.</span>}
                  {r.name}
                </p>
                <p className="text-[11px] text-gray-500 truncate">{r.email}</p>
              </div>
            </div>
            <RolePill role={r.role} />
          </div>
        ))}
      </div>
    )}
  </div>
);

const SettingsPanel = ({ settings, onDeadline }) => {
  const { deadline, reminder, setReminder, requireAll, setRequireAll, signingOrder, setSigningOrder } = settings;
  const orderHint = signingOrder
    ? "Recipients are notified one at a time, in list order. Drag the handles to reorder."
    : "All selected recipients are notified at once.";
  return (
    <div>
      <CardTitle icon={Settings}>Settings</CardTitle>
      <div className="space-y-2">
        <div>
          <Label>Signing Deadline</Label>
          <IconField icon={Calendar}>
            <input
              type="date"
              value={deadline}
              min={toDateInput(new Date())}
              onChange={(e) => onDeadline(e.target.value)}
              className="text-sm text-gray-800"
              style={bareCtl}
            />
          </IconField>
        </div>
        <div>
          <Label>Reminder Frequency</Label>
          <IconField icon={Bell} chevron>
            <select
              value={reminder}
              onChange={(e) => setReminder(e.target.value)}
              className="text-sm text-gray-800"
              style={bareSelect}
            >
              {REMINDERS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </IconField>
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none pt-0.5">
          <input type="checkbox" checked={requireAll} onChange={(e) => setRequireAll(e.target.checked)} className="w-4 h-4 accent-indigo-600" />
          Require all recipients to sign
          <span title="The request is only complete once every selected recipient has finished their step.">
            <Info size={13} className="text-gray-400" />
          </span>
        </label>

        <div>
          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
            <input type="checkbox" checked={signingOrder} onChange={() => setSigningOrder(!signingOrder)} className="w-4 h-4 accent-indigo-600" />
            Set signing order
          </label>
          <p className="text-[11px] text-gray-500 ml-6 mt-0.5 leading-snug">{orderHint}</p>
        </div>
      </div>
    </div>
  );
};

const SecureCard = ({ onLearnMore }) => (
  <div className="bg-indigo-50/70 rounded-xl p-2.5 flex gap-3">
    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0">
      <ShieldCheck size={18} className="text-indigo-600" />
    </div>
    <div>
      <p className="text-xs font-semibold text-gray-900">Secure &amp; Compliant</p>
      <p className="text-[11px] text-gray-600 leading-relaxed mt-0.5">
        All recipients are authenticated using quantum-safe cryptography and their signatures are anchored on blockchain/IPFS.
      </p>
      <button
        type="button"
        onClick={onLearnMore}
        className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700 mt-1.5 inline-flex items-center gap-1"
      >
        Learn more <ArrowRight size={11} />
      </button>
    </div>
  </div>
);

const StatusMessage = ({ error, notice }) => {
  if (error) return <span className="text-red-500">{error}</span>;
  if (notice) return <span className="text-green-600">{notice}</span>;
  return null;
};

const FooterBar = ({ error, notice, onCancel, onNext }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
    <button
      type="button"
      onClick={onCancel}
      className="h-9 px-6 border border-indigo-300 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-medium transition"
    >
      Cancel
    </button>

    <div className="flex-1 min-w-[160px] text-center text-xs">
      <StatusMessage error={error} notice={notice} />
    </div>

    <button
      type="button"
      onClick={onNext}
      className="flex items-center gap-2 h-9 px-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition"
    >
      <ArrowRight size={14} /> Continue to Review &amp; Send
    </button>
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function AddRecipients() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const uploadedFile = state?.uploadedFile;
  const meta = state?.requestMeta || {}; // title / message / deadline / reminder / requireAll from step 1
  const currentStep = 1;

  const [tab, setTab] = useState("individual");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [orgQuery, setOrgQuery] = useState("");

  const status = useStatus();
  const { error, notice, setError, setNotice, fail, succeed } = status;
  const orgUsers = useOrgUsers();
  const settings = useSettings(meta);
  const list = useRecipientList(status);
  const { recipients } = list;

  const selected = recipients.filter((r) => r.selected);

  /* ---------------- add / edit one recipient ---------------- */

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setError("");
  };

  const submitForm = () => {
    const result = validateRecipientForm(form, recipients, editingId);
    if (result.error) return fail(result.error);

    list.save(result.data, editingId);
    succeed(`${result.data.name} ${editingId ? "updated" : "added"}.`);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const startEdit = (r) => {
    setTab("individual");
    setEditingId(r.id);
    setForm(formFromRecipient(r));
    setError("");
    setNotice("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError("");
  };

  const deleteRecipient = (id) => {
    list.remove(id);
    if (editingId === id) cancelEdit();
  };

  /* ---------------- continue ---------------- */

  const handleNext = () => {
    setNotice("");
    const problem = nextProblem({ uploadedFile, selectedCount: selected.length, deadline: settings.deadline });
    if (problem) return fail(problem);

    setError("");
    navigate("/review-and-send", {
      state: {
        uploadedFile,
        recipients: selected,
        message: meta.message || "",
        signingOrder: settings.signingOrder,
        requestMeta: { ...meta, deadline: settings.deadline, reminder: settings.reminder, requireAll: settings.requireAll },
      },
    });
  };

  const goUpload = () => navigate("/request-signatures");

  return (
    <div className="w-full space-y-3" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <Stepper currentStep={currentStep} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_290px] gap-3 items-start">
        {/* ============================ LEFT ============================ */}
        <Card className="space-y-3">
          <div>
            <CardTitle icon={User}>Recipient Details</CardTitle>
            <RecipientTabs tab={tab} onSelect={setTab} />
            {tab === "individual" && (
              <IndividualForm form={form} setField={setField} editingId={editingId} onCancel={cancelEdit} onSubmit={submitForm} />
            )}
            {tab !== "individual" && (
              <OrgPicker orgUsers={orgUsers} query={orgQuery} onQuery={setOrgQuery} recipients={recipients} onAdd={list.addOrgUser} />
            )}
          </div>

          <CsvUpload onFile={list.importCsv} />

          <RecipientList
            list={list}
            selected={selected}
            signingOrder={settings.signingOrder}
            onEdit={startEdit}
            onDelete={deleteRecipient}
          />
        </Card>

        {/* ============================ RIGHT ============================ */}
        <Card className="space-y-3">
          <DocumentSummary uploadedFile={uploadedFile} onChange={goUpload} />
          <hr className="border-gray-100" />
          <SelectedRecipients selected={selected} signingOrder={settings.signingOrder} />
          <hr className="border-gray-100" />
          <SettingsPanel
            settings={settings}
            onDeadline={(v) => {
              settings.setDeadline(v);
              setError("");
            }}
          />
          <SecureCard onLearnMore={() => navigate("/settings/post-quantum")} />
        </Card>
      </div>

      <FooterBar error={error} notice={notice} onCancel={goUpload} onNext={handleNext} />
    </div>
  );
}